import React, { type JSX } from "react";
import type { ConversationFilters } from "../types";
import type { DebouncedField } from "./filterDraft";
import type { FilterValue } from "./useConversationList";

export const INPUT_CLASS =
  "w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500";
export const LABEL_CLASS = "block text-sm font-medium text-gray-700 mb-1";

export type OnFilterChange = (field: keyof ConversationFilters, value: FilterValue) => void;

export type OnDraftChange = (field: DebouncedField, value: string) => void;

/**
 * A free-text filter that applies once typing pauses.
 *
 * Reads its value from the draft rather than the applied filters, so the box
 * shows what was typed while the debounce is still pending.
 */
export function DraftTextFilter({
  label,
  field,
  value,
  placeholder,
  type = "text",
  onDraftChange,
}: {
  label: string;
  field: DebouncedField;
  value: string;
  placeholder?: string;
  type?: string;
  onDraftChange: OnDraftChange;
}): JSX.Element {
  return (
    <div>
      <label className={LABEL_CLASS}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onDraftChange(field, e.target.value)}
        placeholder={placeholder}
        className={INPUT_CLASS}
      />
    </div>
  );
}

/** Free-text or numeric filter input. */
export function TextFilter({
  label,
  field,
  value,
  placeholder,
  type = "text",
  onFilterChange,
}: {
  label: string;
  field: keyof ConversationFilters;
  value: string | number;
  placeholder?: string;
  type?: string;
  onFilterChange: OnFilterChange;
}): JSX.Element {
  return (
    <div>
      <label className={LABEL_CLASS}>{label}</label>
      <input
        type={type}
        value={value}
        onChange={(e) => onFilterChange(field, e.target.value)}
        placeholder={placeholder}
        className={INPUT_CLASS}
      />
    </div>
  );
}

/** Fixed-option filter, e.g. environment or source. */
export function SelectFilter({
  label,
  field,
  value,
  options,
  onFilterChange,
}: {
  label: string;
  field: keyof ConversationFilters;
  value: string;
  options: { value: string; label: string }[];
  onFilterChange: OnFilterChange;
}): JSX.Element {
  return (
    <div>
      <label className={LABEL_CLASS}>{label}</label>
      <select
        value={value}
        onChange={(e) => onFilterChange(field, e.target.value)}
        className={INPUT_CLASS}
      >
        {options.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  );
}

/**
 * On / off filter over a boolean flag.
 *
 * Unlike BooleanFilter there is no "either" state: the flag is a switch the
 * viewer either has on or off, so a checkbox says more than a three-way select.
 */
export function CheckboxFilter({
  field,
  label,
  checked,
  onFilterChange,
}: {
  field: keyof ConversationFilters;
  label: string;
  checked: boolean;
  onFilterChange: OnFilterChange;
}): JSX.Element {
  const inputId = `filter-${field}`;
  return (
    <div className="flex items-center gap-2">
      <input
        type="checkbox"
        id={inputId}
        checked={checked}
        onChange={(e) => onFilterChange(field, e.target.checked)}
        className="h-4 w-4 rounded-sm border-gray-300 text-blue-600 focus:ring-blue-500"
      />
      <label htmlFor={inputId} className="text-sm font-medium text-gray-700">
        {label}
      </label>
    </div>
  );
}

/** Yes / no / either filter over a boolean flag. */
export function BooleanFilter({
  label,
  field,
  value,
  trueLabel,
  falseLabel,
  onFilterChange,
}: {
  label: string;
  field: keyof ConversationFilters;
  value: boolean | undefined;
  trueLabel: string;
  falseLabel: string;
  onFilterChange: OnFilterChange;
}): JSX.Element {
  return (
    <div>
      <label className={LABEL_CLASS}>{label}</label>
      <select
        value={value === undefined ? "" : value ? "true" : "false"}
        onChange={(e) =>
          onFilterChange(field, e.target.value === "" ? undefined : e.target.value === "true")
        }
        className={INPUT_CLASS}
      >
        <option value="">All Conversations</option>
        <option value="true">{trueLabel}</option>
        <option value="false">{falseLabel}</option>
      </select>
    </div>
  );
}
